import * as React from 'react';
import { PaymentDTO, Res } from 'core/api';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { ActionButtonDefinition, PosScreenClassComponent } from 'pos/ui';
import { Cols, Col } from 'core/ui';
import { RefundPaymentsProcessor } from './RefundExternalPaymentService.RefundPaymentsProcessor';
import { getTransactionAmount } from './ExternalPaymentsService.Common';
import { Modal } from 'core/ui-next/modal';

type Props = {
	actions?: UI.DialogActions<void>
};

class ConfirmIgnorePaymentDialog extends UI.StatelessComponent<Props> {

	onComponentDidMount(): void { }
	onComponentWillUnmount(): void { }

	cancel() {
		this.props.actions!.cancel();
	}

	accept = async () => {
		this.props.actions!.closeWith();
	};

	render() {

		return <UI.Dialog>
			<UI.DialogHeader>
				<h4 className='modal-title'>
					{Res.Common.WARNING()}
				</h4>
			</UI.DialogHeader>
			<UI.DialogBody>
				<div className='alert alert-danger text-16 m-t-md' style={{ display: 'flex', alignItems: 'baseline' }}>
					<span className='fa fa-exclamation-circle m-r-sm'></span>{
						Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_PAY_IGNORE_CONSEQUENCES_CONFIRM()
					}
				</div>

				<div className='m-b-xl m-t-xl '>
					<UI.Button buttonStyle='danger2' className='btn-block btn-lg' style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					}} onClick={() => this.accept()}>
						<Images.Warning className='m-r-sm' />
						<span>{Res.Web.EXTERNAL_PAYMENT_IGNORE_BTN()}</span>
					</UI.Button>
				</div>

			</UI.DialogBody>
			<UI.DialogFooter>
				<UI.Button className='auto-focus' buttonStyle='default' onClick={() => this.cancel()}>{Res.Common.CANCEL_BTN()}</UI.Button>
			</UI.DialogFooter>
		</UI.Dialog>;
	}
}

type RefundStatus = 'pending' | 'success' | 'error' | 'ignored-error';
export type RefundPaymentStatus = { payment: PaymentDTO, status: RefundStatus }

function PaymentsTable({
	disableActionButtons,
	paymentStatuses,
	onRetryClick,
	onIgnoreClick
}: {
	disableActionButtons: boolean,
	paymentStatuses: RefundPaymentStatus[],
	onRetryClick: () => Promise<void>,
	onIgnoreClick?: () => Promise<void>
}) {
	const statusIcon: Record<RefundStatus, React.ReactNode> = {
		// Estos colores con Chakra ya los pillaremos en el futuro cuando se haga esta pantalla
		'pending': <Images.Retry color='#61AAED' />,
		'success': <Images.Ok color='#00BA4C' />,
		'error': <Images.Cancel color='#FF8A95' />,
		'ignored-error': <Images.Cancel color='#FF8A95' />
	};

	const lastSuccessOrErrorRef = React.useRef<HTMLDivElement | null>(null);

	// Busco índice del último con status success o error
	const lastIdx = [...paymentStatuses]
		.map((r, i) => ({ r, i }))
		.reverse()
		.find(({ r }) => r.status === 'success' || r.status === 'error')?.i;

	// Ejecutar scroll cuando cambian los datos y el ref está listo
	React.useLayoutEffect(() => {
		if (lastSuccessOrErrorRef.current) {
			lastSuccessOrErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, [paymentStatuses, lastIdx]);

	return <>
		<Cols className='font-extra-bold'>
			<Col width='36px'>
			</Col>
			<Col width='*'>
				{Res.Web.REFUND_PAYMENT_METHOD_CLM()}
			</Col>
			<Col width='70px' className='text-right'>
				{Res.Web.EXTERNAL_PAYMENT_TRANSACTION_AMOUNT_CLM()}
			</Col>
			<Col width='70px' className='text-right'>
				{Res.Web.EXTERNAL_PAYMENT_TIP_AMOUNT_CLM()}
			</Col>
		</Cols>
		{
			paymentStatuses.map((row, idx) => {
				return (
					<div key={idx} ref={idx === lastIdx ? lastSuccessOrErrorRef : null} style={{ borderTop: '1px solid #e4e5e7' }}  >
						<Cols>
							<Col width='36px'>
								{statusIcon[row.status]}
							</Col>
							<Col width='*' style={{ alignSelf: 'center' }}>
								{row.payment.MethodName}
							</Col>
							<Col width='70px' className='text-right' style={{ alignSelf: 'center' }}>
								{UI.Format.asMoney(row.payment.Amount)}
							</Col>
							<Col width='70px' className='text-right' style={{ alignSelf: 'center' }}>
								{UI.Format.asMoney(row.payment.TipAmount)}
							</Col>
						</Cols>

						{
							row.status === 'error' && (

								<div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '8px' }}>

									<button type='button' className='btn btn-info' onClick={onRetryClick} disabled={disableActionButtons}>
										<span className='fa fa-refresh m-r-sm' />
										{Res.Web.EXTERNAL_PAYMENT_RETRY_REFUND_BTN()}
									</button>

									{
										onIgnoreClick && (
											<button type='button' className='btn btn-danger2' onClick={onIgnoreClick} disabled={disableActionButtons}>
												<span className='fa fa-exclamation-triangle m-r-sm' />
												{Res.Web.EXTERNAL_PAYMENT_IGNORE_BTN()}
											</button>
										)
									}
								</div>

							)
						}

					</div>
				);
			})}

	</>;
}

type RefundPaymentsProps = {
	revertPayment: (payment: PaymentDTO) => Promise<{ ok: boolean, refundPayment: PaymentDTO }>,
	discardPayment: (payment: PaymentDTO) => Promise<void>,
	payments: PaymentDTO[]
} & Modal.Actions<PaymentDTO[]>


export class RefundPaymentsScreen extends PosScreenClassComponent<RefundPaymentsProps, { operationInProgress: boolean }> {

	private processor: RefundPaymentsProcessor;

	constructor(props: RefundPaymentsProps) {
		super(props);

		this.withOperationInProgress.bind(this);

		const onRevertPayment = (payment: PaymentDTO): Promise<{ ok: boolean; refundPayment: PaymentDTO }> => {
			return this.withOperationInProgress(() => props.revertPayment(payment));
		};

		const onDiscardPayment = (payment: PaymentDTO): Promise<void> => {
			return this.withOperationInProgress(() => props.discardPayment(payment));
		};

		this.processor = new RefundPaymentsProcessor(props.payments, onRevertPayment, onDiscardPayment, () => {
			if (this.processor.hasFinished())
				this.close(this.processor.getPayments());
			else
				this.forceUpdate();
		});
	}

	createInitialState(_props: RefundPaymentsProps): Nullable<{ operationInProgress: boolean; }> {
		return { operationInProgress: false };
	}

	private withOperationInProgress<T>(fn: () => Promise<T>): Promise<T> {
		return new Promise<T>(resolve => {
			this.setState({ operationInProgress: true }, async () => {
				try {
					const result = await fn();
					resolve(result);
				}
				finally {
					this.setState({ operationInProgress: false });
				}
			});
		});
	}

	protected getScreenId(): string {
		return 'REFUND_PAYMENTS';
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	protected getTitle(): string {
		return Res.Web.REFUND_PAYMENT_TITLE();
	}

	protected getImage() {
		return Images.RefundTicket;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		const backButton: Nullable<ActionButtonDefinition> = this.processor.canIgnoreLastPayment() ? null : this.cancelButton();
		return [backButton, null, null, null, null, null, null];
	}

	protected isCloseHeaderButtonHidden(): boolean {
		return this.processor.canIgnoreLastPayment();
	}

	protected useKeypad(): boolean {
		return false;
	}

	protected useKeyboard(): boolean {
		return false;
	}

	protected getContent(): React.ReactNode {

		const hasBeenStarted = this.processor.hasBeenStarted();
		const lastErrorPayment = this.processor.getLastPaymentWithError();

		const onIgnore = this.processor.canIgnoreLastPayment()
			? async () => {
				const result = await UI.Modal.showCustom(<ConfirmIgnorePaymentDialog />);
				if (result.ok)
					await this.withOperationInProgress(() => this.processor.ignoreLastPayment());
			}
			: undefined;

		return <div className='p-m'>
			{
				lastErrorPayment
					? <p className='alert alert-danger m-b-md text-16' style={{ display: 'flex', alignItems: 'baseline' }}>
						<span className='fa fa-exclamation-circle m-r-sm'></span>{
							Res.Web.EXTERNAL_PAYMENT_REFUND_FAILURE_DESCRIPTION(getTransactionAmount(lastErrorPayment), lastErrorPayment.MethodName)
						}
					</p>
					: <p className='alert alert-info m-b-md text-16' style={{ display: 'flex', alignItems: 'baseline' }}>
						<span className='fa fa-exclamation-circle m-r-sm'></span>{
							Res.Web.EXTERNAL_PAYMENT_CONFIRM_REFUND_DESCRIPTION()
						}
					</p>
			}

			{/* OJO: Los componentes quedan un poco juntos en PC, pero es para que en el Castles S1F2 quepan sin scroll */}

			<UI.Flex.Vertical>
				<UI.Flex.Item style={{ maxHeight: '356px', overflowY: 'auto' }}>
					<PaymentsTable paymentStatuses={this.processor.getStatuses()} onRetryClick={() => this.processor.retryLastPayment()} onIgnoreClick={onIgnore} disableActionButtons={this.state.operationInProgress} />
				</UI.Flex.Item>
				<UI.Flex.Item >
					{
						hasBeenStarted
							? null
							: <div className='m-t-xl'>
								<UI.Button buttonStyle='primary' disabled={this.state.operationInProgress} className='btn-block btn-lg' style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center'
								}} onClick={() => this.processor.start()}>
									<Images.Ok className='m-r-sm' />
									<span>{Res.Web.EXTERNAL_PAYMENT_CONFIRM_REFUND_BTN()}</span>
								</UI.Button>
							</div>
					}
				</UI.Flex.Item>
			</UI.Flex.Vertical>
		</div>;
	}

}
